import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { fetchChannelsFromGitHub, normalizeChannelList } from '../utils/fetchChannelsFromGitHub.mjs';
import { fetchChannelsFromFirebaseCache } from '../utils/fetchChannelsFromFirebaseCache';
import { loadAllCountries, loadAllCategories } from '../utils/loadAllCountries';
import localCountriesMetadata from '../data/channels/raw/countries_metadata.json';
import { applyHealthToChannelList, setHealthMap } from '../utils/streamHealth';
import { fetchChannelHealthFromFirebase } from '../utils/fetchChannelHealthFromFirebase';

const ChannelDataContext = createContext();

/** Set to "github" to skip Firebase cache (e.g. local dev). Default: try Firebase snapshot first. */
const CHANNEL_SOURCE = (process.env.REACT_APP_CHANNELS_SOURCE || 'firebase').toLowerCase();

function getChannelKey(channel) {
  if (!channel || typeof channel !== 'object') return '';
  const stream = Array.isArray(channel.iptv_urls) ? channel.iptv_urls[0] : '';
  const youtube = Array.isArray(channel.youtube_urls) ? channel.youtube_urls[0] : '';
  return `${String(channel.name || '').trim().toLowerCase()}|${stream || ''}|${youtube || ''}`;
}

function mergeChannelLists(primary = [], fallback = []) {
  const out = [];
  const seen = new Set();
  [...primary, ...fallback].forEach((channel) => {
    const key = getChannelKey(channel);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(channel);
  });
  return out;
}

export function ChannelDataProvider({ children }) {
  const [countries, setCountries] = useState(() => loadAllCountries());
  const [categories, setCategories] = useState(() => loadAllCategories());
  const [countriesMetadata, setCountriesMetadata] = useState(localCountriesMetadata);
  const [channelsUpdatedAt, setChannelsUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadChannels = useCallback(() => {
    setLoading(true);
    setError(null);

    const applyPayload = (data, updatedAt) => {
      const localCountries = loadAllCountries();
      const normalizedCountries = {};
      const countryCodes = new Set([
        ...Object.keys(localCountries || {}),
        ...Object.keys(data.countries || {}),
      ]);
      countryCodes.forEach((code) => {
        const remoteList = normalizeChannelList(Array.isArray(data.countries?.[code]) ? data.countries[code] : []);
        const localList = normalizeChannelList(Array.isArray(localCountries?.[code]) ? localCountries[code] : []);
        const merged = mergeChannelLists(remoteList, localList);
        /* Health pass: drop channels with all iptv URLs known dead (and no YouTube fallback)
           and reorder remaining iptv_urls so known-good URLs are tried first. */
        normalizedCountries[code] = applyHealthToChannelList(merged);
      });
      const normalizedCategories = {};
      Object.entries(data.categories || {}).forEach(([name, list]) => {
        normalizedCategories[name] = applyHealthToChannelList(
          normalizeChannelList(Array.isArray(list) ? list : [])
        );
      });
      setCountries(normalizedCountries);
      setCategories(normalizedCategories);
      setCountriesMetadata(data.countriesMetadata);
      setChannelsUpdatedAt(updatedAt ?? null);
      setError(null);
    };

    /* Hydrate the runtime health registry before applying any channel payload,
       so the very first applyPayload uses the latest health data and the user
       never sees dead channels even on a cold load. Failures here are
       non-fatal — channels just stay in their original order, unfiltered. */
    const hydrateHealth = async () => {
      try {
        const health = await fetchChannelHealthFromFirebase();
        if (health) setHealthMap(health.results, health.generatedAt);
      } catch (_) { /* noop */ }
    };

    const run = async () => {
      const healthPromise = hydrateHealth();

      if (CHANNEL_SOURCE !== 'github') {
        try {
          const cached = await fetchChannelsFromFirebaseCache();
          if (cached) {
            await healthPromise;
            applyPayload(cached, cached.updatedAt);
            return;
          }
        } catch {
          /* no cache or rules/network — fall back */
        }
      }
      const data = await fetchChannelsFromGitHub();
      await healthPromise;
      applyPayload(data, null);
    };

    run()
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const allCountries = useMemo(() => countries || {}, [countries]);
  const allCategories = useMemo(() => categories || {}, [categories]);
  const metadata = useMemo(() => countriesMetadata || localCountriesMetadata, [countriesMetadata]);

  const memoizedAllChannels = useMemo(() => {
    return Object.values(allCountries).flat();
  }, [allCountries]);

  const value = {
    allCountries,
    allCategories,
    countriesMetadata: metadata,
    memoizedAllChannels,
    loading,
    error,
    channelsUpdatedAt,
    refresh: loadChannels,
  };

  return (
    <ChannelDataContext.Provider value={value}>
      {children}
    </ChannelDataContext.Provider>
  );
}

export function useChannelData() {
  const ctx = useContext(ChannelDataContext);
  if (!ctx) throw new Error('useChannelData must be used within ChannelDataProvider');
  return ctx;
}

export default ChannelDataContext;
